import { useMemo, useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useCurrency } from '@/contexts';
import { useTranslation } from '@/hooks/useTranslation';

interface BudgetBlockProps {
  income: number;
  expenses: number;
  previousIncome?: number;
  previousExpenses?: number;
  isLoading?: boolean;
}

// Animated counter hook
function useAnimatedCounter(end: number, duration: number = 1000): number {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const startTime = useRef<number | null>(null);

  useEffect(() => {
    startTime.current = null;
    const startValue = countRef.current;

    const animate = (timestamp: number) => {
      if (!startTime.current) startTime.current = timestamp;
      const progress = Math.min((timestamp - startTime.current) / duration, 1);

      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (end - startValue) * easeOutCubic;

      setCount(current);
      countRef.current = current;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration]);

  return count;
}

interface BudgetCardProps {
  title: string;
  value: number;
  icon: React.ElementType;
  accentColor: string;
  bgGradient: string;
  progress?: number; // 0-100
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  subtitle?: string;
  exceededLabel?: string;
}

function BudgetCard({
  title,
  value,
  icon: Icon,
  accentColor,
  bgGradient,
  progress,
  trend,
  trendValue,
  subtitle,
  exceededLabel,
}: BudgetCardProps) {
  const { formatAmount, isSecretMode } = useCurrency();
  const animatedValue = useAnimatedCounter(value, 1000);

  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 overflow-hidden group hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-black/30 transition-all duration-300">
      {/* Background Gradient Glow */}
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-[0.08] group-hover:opacity-[0.12] transition-opacity duration-500"
        style={{ background: bgGradient }}
      />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${accentColor}15` }}
          >
            <Icon className="w-5 h-5" style={{ color: accentColor }} />
          </div>
          {trend && trendValue && (
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${
                trend === 'up'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                  : trend === 'down'
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-400'
              }`}
            >
              {trend === 'up' && <ArrowUpRight className="w-3 h-3" />}
              {trend === 'down' && <ArrowDownRight className="w-3 h-3" />}
              {trendValue}
            </div>
          )}
        </div>

        {/* Value */}
        <p className="text-2xl font-black text-gray-900 dark:text-white tracking-tight tabular-nums mb-1">
          {isSecretMode ? '•••' : formatAmount(animatedValue)}
        </p>

        {/* Title */}
        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em]">
          {title}
        </p>

        {/* Subtitle if any */}
        {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>}

        {/* Progress Bar */}
        {progress !== undefined && (
          <div className="mt-4">
            <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(progress, 100)}%`,
                  background:
                    progress > 100 ? 'linear-gradient(90deg, #ef4444, #f87171)' : bgGradient,
                }}
              />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
                {progress.toFixed(0)}%
              </span>
              {progress > 100 && exceededLabel && (
                <span className="text-[10px] font-semibold text-red-500">{exceededLabel}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function BudgetBlock({
  income,
  expenses,
  previousIncome,
  previousExpenses,
  isLoading = false,
}: BudgetBlockProps) {
  const { t } = useTranslation();

  const remaining = income - expenses;
  const expenseProgress = income > 0 ? (expenses / income) * 100 : 0;

  // Calculate trends
  const incomeTrend = useMemo((): { trend: 'up' | 'down' | 'neutral'; value: string } | null => {
    if (!previousIncome) return null;
    const change = ((income - previousIncome) / Math.abs(previousIncome || 1)) * 100;
    const trend: 'up' | 'down' | 'neutral' = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';
    return {
      trend,
      value: `${change > 0 ? '+' : ''}${change.toFixed(0)}%`,
    };
  }, [income, previousIncome]);

  const expenseTrend = useMemo((): { trend: 'up' | 'down' | 'neutral'; value: string } | null => {
    if (!previousExpenses) return null;
    const change = ((expenses - previousExpenses) / Math.abs(previousExpenses || 1)) * 100;
    // For expenses, down is good, up is bad
    const trend: 'up' | 'down' | 'neutral' = change < 0 ? 'down' : change > 0 ? 'up' : 'neutral';
    return {
      trend,
      value: `${change > 0 ? '+' : ''}${change.toFixed(0)}%`,
    };
  }, [expenses, previousExpenses]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse"
          >
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" />
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Income Card */}
        <BudgetCard
          title={t('dashboard.stats.income')}
          value={income}
          icon={TrendingUp}
          accentColor="#16c47f"
          bgGradient="linear-gradient(135deg, #16c47f, #10b981)"
          trend={incomeTrend?.trend}
          trendValue={incomeTrend?.value}
        />

        {/* Expenses Card */}
        <BudgetCard
          title={t('dashboard.stats.expenses')}
          value={expenses}
          icon={TrendingDown}
          accentColor="#78909C"
          bgGradient="linear-gradient(135deg, #78909C, #607D8B)"
          progress={expenseProgress}
          trend={expenseTrend?.trend}
          trendValue={expenseTrend?.value}
          subtitle={
            income > 0
              ? `${expenseProgress.toFixed(0)}% ${t('dashboard.budget.ofIncome' as any)}`
              : undefined
          }
          exceededLabel={t('dashboard.budget.exceeded' as any)}
        />

        {/* Remaining Card */}
        <BudgetCard
          title={t('dashboard.budget.remaining' as any)}
          value={remaining}
          icon={Wallet}
          accentColor={remaining >= 0 ? '#3b82f6' : '#ef4444'}
          bgGradient={
            remaining >= 0
              ? 'linear-gradient(135deg, #3b82f6, #6366f1)'
              : 'linear-gradient(135deg, #ef4444, #f87171)'
          }
          subtitle={
            remaining >= 0
              ? t('dashboard.budget.availableBalance' as any)
              : t('dashboard.budget.expensesOverIncome' as any)
          }
        />
      </div>
    </div>
  );
}
